import React, { useRef, useState, useCallback } from 'react';
import AvatarEditor from 'react-avatar-editor';
import styles from './AvatarEditor.module.scss';
import { Icon } from '../../../../components/Icon';
import CmsAPI from '../../../../../common/apis/CmsAPI';
import avatarPlaceholder from '../../../../assets/avatarPlaceholder.png';
import { useDropzone } from 'react-dropzone';
import { useUserContext } from '../../../../context/UserContext';
import { useTranslation } from 'react-i18next';
import { ToastNotification } from '../../../../components/Toast/Toast';
import { CustomButton } from '../../../../components/Button/Button';

const avatarStateInit = {
  image: '',
  position: { x: 0.5, y: 0.5 },
  scale: 1.2,
  rotate: 0,
  borderRadius: 50,
  preview: null,
  width: 320,
  height: 320,
};

export const AvatarEditorModal = ({ handleCloseModal, handleChangeAvatar, isMobileView }) => {
  const [avatarState, setAvatarState] = useState(avatarStateInit);
  const [isUpload, setIsUpload] = useState(false);

  const { t } = useTranslation();

  const { updateUserData } = useUserContext();

  const onDrop = useCallback(async ([acceptedFile]) => {
    if (acceptedFile) {
      setAvatarState({ ...avatarState, image: acceptedFile });
      setIsUpload(true);

      return;
    }

    ToastNotification({
      message: t('general.toast.format_not_supported'),
      isMobileView: isMobileView,
    });
  }, []);

  const { getRootProps, getInputProps, open } = useDropzone({
    accept: { 'image/*': ['.jpeg', '.png', '.jpg'] },
    onDrop,
  });

  let editorRef = useRef();

  const setEditorRef = editor => {
    if (editor) {
      editorRef = editor;
    }
  };

  const handleScale = e => {
    const scale = parseFloat(e.target.value);
    setAvatarState({ ...avatarState, scale });
  };

  const inputElements = document.querySelectorAll('[type="range"]');

  const handleInput = inputElement => {
    let isChanging = false;

    const setCSSProperty = () => {
      const percent = ((inputElement.value - inputElement.min) / (inputElement.max - inputElement.min)) * 100;
      inputElement.style.setProperty('--webkitProgressPercent', `${percent}%`);
    };

    const handleMove = () => {
      if (!isChanging) return;
      setCSSProperty();
    };
    const handleUpAndLeave = () => (isChanging = false);
    const handleDown = () => (isChanging = true);

    inputElement.addEventListener('mousemove', handleMove);
    inputElement.addEventListener('mousedown', handleDown);
    inputElement.addEventListener('mouseup', handleUpAndLeave);
    inputElement.addEventListener('mouseleave', handleUpAndLeave);
    inputElement.addEventListener('click', setCSSProperty);

    // Init input
    setCSSProperty();
  };

  inputElements.forEach(handleInput);

  const handlePositionChange = position => {
    setAvatarState({ ...avatarState, position });
  };

  const handleSave = async () => {
    const img = editorRef.getImageScaledToCanvas().toDataURL();
    const rect = editorRef.getCroppingRect();
    setAvatarState({
      ...avatarState,
      preview: {
        img,
        rect,
        scale: avatarState.scale,
        width: avatarState.width,
        height: avatarState.height,
        borderRadius: avatarState.borderRadius,
      },
    });

    try {
      // Upload image to S3 through CMS
      const data = await CmsAPI.updateUser({ avatar: img });
      console.log('updateUser response', data);
      await updateUserData();
      handleChangeAvatar(img);
      setIsUpload(false);
      handleCloseModal();
    } catch (e) {
      setIsUpload(false);
      handleCloseModal();
      ToastNotification({
        message: t('general.toast.error_uploading_profile_img'),
        isMobileView: isMobileView,
      });
      console.log(e);
    }
  };

  return (
    <div className={styles.modalContainer}>
      <div className={styles.modalHeader}>
        <h2 className={styles.modalTitle}>{t('account.settings.profile.profile_picture.upload.title')}</h2>
        <Icon src="close" className={styles.closeIcon} onClick={handleCloseModal} />
      </div>
      {isUpload ? (
        <div className={styles.canvasContainer}>
          <AvatarEditor
            ref={setEditorRef}
            scale={parseFloat(avatarState.scale)}
            // className={styles.avatarEditor}
            width={avatarState.width}
            height={avatarState.height}
            position={avatarState.position}
            onPositionChange={handlePositionChange}
            rotate={parseFloat(avatarState.rotate)}
            border={24}
            color={[255, 255, 255, 0.3]}
            borderRadius={avatarState.width / (50 / avatarState.borderRadius)}
            // style={{
            //   backgroundColor: 'red !important'
            // }}
            // onLoadFailure={this.logCallback.bind(this, "onLoadFailed")}
            // onLoadSuccess={this.logCallback.bind(this, "onLoadSuccess")}
            // onImageReady={this.logCallback.bind(this, "onImageReady")}
            image={avatarState.image}
          />
        </div>
      ) : (
        <div
          className={styles.avatarImage}
          style={{
            marginBottom: !avatarState.image ? '24px' : '0px',
          }}
          {...getRootProps()}
        >
          <p className={styles.modalDescription}>{t('account.settings.profile.profile_picture.upload.drag')}</p>
          <div>
            {avatarState.preview ? (
              <img
                src={avatarState.preview}
                style={{
                  borderRadius: `${avatarState.width / (50 / avatarState.borderRadius)}px`,
                }}
                alt="img"
              />
            ) : (
              <img src={avatarPlaceholder} className={styles.avatarPlaceholder} alt={t('clip_organizer.add_modal.avatar')} />
            )}
            <input className={styles.dragAndDropInput} {...getInputProps()} />
          </div>
        </div>
      )}
      {avatarState.image && (
        <div className={styles.zoomContainer}>
          <div>{t('account.settings.zoom_in_out')}</div>
          <input name="scale" type="range" onChange={handleScale} min="1.2" max="2" step="0.01" defaultValue="1.2" />
        </div>
      )}
      <div className={styles.modalButtonContainer}>
        {avatarState.image ? (
          <CustomButton
            label={t('account.settings.save_profile_picture')}
            variant="primary"
            className={styles.modalButton}
            onClick={handleSave}
          />
        ) : (
          <CustomButton
            label={t('account.settings.profile.profile_picture.upload.button')}
            variant="primary"
            className={styles.modalButton}
            onClick={open}
          />
        )}
      </div>
    </div>
  );
};
